import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  useStepperReturn,
} from '@ais-ui/ui-core';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { StepperFooter } from './StepperFooter';
import { useLocalization } from '@ais-ui/locale-management';
import { useCreateReportContext } from './CreateReportContext';
import React, { useEffect } from 'react';
import { StepperHeadline } from './StepperHeadline';

const reportTemplateNamingFormSchema = z.object({
  template_name: z.string().min(1, 'Template name is required'),
  report_name: z.string().optional(),
});

type reportTemplateNamingValues = z.infer<
  typeof reportTemplateNamingFormSchema
>;

interface NamingStepProps {
  onPrevious: ((event: React.MouseEvent<HTMLButtonElement>) => void) | null;
}

export const NamingStep = ({ onPrevious }: NamingStepProps) => {
  const { messages } = useLocalization();

  const {
    stepperProps,
    reportTemplateName,
    firstReportName,
    setReportTemplateName,
    setFirstReportName,
  } = useCreateReportContext();

  const form = useForm<reportTemplateNamingValues>({
    resolver: zodResolver(reportTemplateNamingFormSchema),
    defaultValues: {
      template_name: reportTemplateName,
      report_name: firstReportName,
    },
  });

  // Add two-way binding: sync form changes back to context state in real-time
  useEffect(() => {
    const subscription = form.watch((values) => {
      // Update context state when form values change
      if (values.template_name !== undefined) {
        setReportTemplateName(values.template_name);
      }
      if (values.report_name !== undefined) {
        setFirstReportName(values.report_name || '');
      }
    });

    return () => subscription.unsubscribe();
  }, [form.watch, setReportTemplateName, setFirstReportName]);

  // Sync context changes back to form (when switching from quick create to wizard)
  useEffect(() => {
    form.setValue('template_name', reportTemplateName);
    form.setValue('report_name', firstReportName);
  }, [reportTemplateName, firstReportName, form.setValue]);

  const onNext = (data: reportTemplateNamingValues) => {
    // Form changes are already synced to context via useEffect above
    // But keep this for explicit confirmation on next step
    setReportTemplateName(data.template_name);
    setFirstReportName(data.report_name || '');

    stepperProps.nextStep();
  };

  return (
    <div className="grid grid-rows-[auto_1fr_auto] h-full">
      <StepperHeadline
        headerText={messages.reportsCreateReportNamingTitle}
        subHeaderText={messages.reportsCreateReportNamingSubtitle}
      />
      <Form {...form}>
        <form className="space-y-8 pt-4 h-full">
          <div className="h-full">
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="template_name"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between">
                      <FormLabel>
                        {messages.reportsCreateReportNamingTemplateNameLabel} *
                      </FormLabel>
                      <InfoIconTooltip>
                        {messages.reportsCreateReportNamingTemplateNameTooltip}
                      </InfoIconTooltip>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Input
                        id="report-template-name"
                        type="text"
                        placeholder={
                          messages.reportsCreateReportNamingTemplateNamePlaceholder
                        }
                        autoFocus
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="report_name"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between">
                      <FormLabel>
                        {messages.reportsCreateReportNamingReportNameLabel}
                      </FormLabel>
                      <InfoIconTooltip>
                        {messages.reportsCreateReportNamingReportNameTooltip}
                      </InfoIconTooltip>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Input
                        type="text"
                        placeholder={
                          messages.reportsCreateReportNamingReportNamePlaceholder
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </form>
      </Form>
      <StepperFooter
        onPrevious={onPrevious}
        onNext={form.handleSubmit(onNext)}
        stepperProps={stepperProps}
      ></StepperFooter>
    </div>
  );
};
